import { useMemo, useRef, useState } from 'react';

/**
 * Lightweight windowed list (no react-window dependency).
 * Pattern: fixed row height + absolute positioning (same idea as FixedSizeList).
 */
export default function VirtualList({
  items,
  itemHeight = 72,
  height = 560,
  overscan = 6,
  className = '',
  getKey,
  renderItem,
}) {
  const scrollerRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const count = items.length;
  const totalHeight = count * itemHeight;

  const { start, end } = useMemo(() => {
    const visible = Math.ceil(height / itemHeight) + overscan * 2;
    const first = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
    return { start: first, end: Math.min(count, first + visible) };
  }, [count, height, itemHeight, overscan, scrollTop]);

  if (count === 0) {
    return null;
  }

  // Small lists: skip windowing overhead.
  if (count <= Math.ceil(height / itemHeight) + overscan) {
    return (
      <div className={className} style={{ maxHeight: height, overflowY: 'auto' }}>
        {items.map((item, index) => (
          <div key={getKey ? getKey(item, index) : index}>{renderItem(item, index)}</div>
        ))}
      </div>
    );
  }

  const slice = items.slice(start, end);

  return (
    <div
      ref={scrollerRef}
      className={className}
      style={{ height, overflowY: 'auto', position: 'relative' }}
      onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        {slice.map((item, offset) => {
          const index = start + offset;
          return (
            <div
              key={getKey ? getKey(item, index) : index}
              style={{
                position: 'absolute',
                top: index * itemHeight,
                left: 0,
                right: 0,
                height: itemHeight,
              }}
            >
              {renderItem(item, index)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
